/**
 * This file creates the interface definitions for the Jarvis Service Data Collection Plugin.
 * The Data Collection Plugin receives telemetry and analytics data from the application, injects
 * additional metadata, repackages into a standard format, and sends to the cloud.
 *
 * This specification only intends to define the Typescript interface that a web app uses,
 * and does not define any native/platform-specific APIs.
 * @packageDocumentation
 * @module DataCollectionPlugin
 */
/**
 * Enumerates the types of ValveFilterErrors that can occur
 */
export var ValveFilterErrorType;
(function (ValveFilterErrorType) {
    /**
     * The filtering of the data failed due to an issue contacting the Valve Controller API
     */
    ValveFilterErrorType["valveControllerAPIError"] = "\u200BvalveControllerAPIError";
    /**
     * The supplied ValveControllerMetadata was invalid (i.e. mismatched parameters, missing assetUnit when providing assetType ”solution”).￼
     */
    ValveFilterErrorType["valveControllerMetadataError"] = "valveControllerMetadataError";
    /**
     * Technical errors. Could be caused by JSONPath/XPath filtering exceptions for example.
     */
    ValveFilterErrorType["filterError"] = "\u200BfilterError";
    /**
     * Failed to parse the supplied tree. The tree could be in an invalid format/incomplete/etc..
     */
    ValveFilterErrorType["invalidTreeError"] = "\u200BinvalidTreeError";
    /**
     * DataCollectionService has not been set up with the minimum required configuration (e.g. client IDs or stack) (first configuration issue found).
     */
    ValveFilterErrorType["configurationError"] = "configurationError";
    /**
     * The supplied tree is not allowed through the valve and cannot be transferred outside of the local network in its entirety.
     */
    ValveFilterErrorType["treeNotAllowed"] = "treeNotAllowed";
})(ValveFilterErrorType || (ValveFilterErrorType = {}));
/**
 * Enumerates the types of asset unit types supported by the Data Collection Plugin
 */
export var AssetUnitType;
(function (AssetUnitType) {
    /**
     * Used when filtering data for a mobile use case
     */
    AssetUnitType["mobile"] = "mobile";
    /**
     * Used when filtering data for a desktop use case
     */
    AssetUnitType["desktop"] = "desktop";
    /**
     * Used when filtering data for a solution use case
     */
    AssetUnitType["solution"] = "solution";
})(AssetUnitType || (AssetUnitType = {}));
/**
 * Enumerates the Event Service Event Names that the Data Collection component subscribes to.
 */
export var DataCollectionEventNames;
(function (DataCollectionEventNames) {
    /**
     * The event name that the data collection service listens for, to filter and send cdm events (such as simpleUi) to the jarvis telemetry service.
     */
    DataCollectionEventNames["cdmEvent"] = "com.hp.jarvis.datacollection.cdm.event";
    /**
     * The name of the event that clients can subscribe to to be informed of the result of internal data collection processing.
     */
    DataCollectionEventNames["cdmEventStatus"] = "com.hp.jarvis.datacollection.cdm.event.status";
})(DataCollectionEventNames || (DataCollectionEventNames = {}));
export var DataCollectionEventAction;
(function (DataCollectionEventAction) {
    DataCollectionEventAction["buildNotification"] = "buildNotification";
    DataCollectionEventAction["filterNotification"] = "filterNotification";
    DataCollectionEventAction["publishNotification"] = "publishNotification";
    DataCollectionEventAction["finish"] = "finish";
})(DataCollectionEventAction || (DataCollectionEventAction = {}));
/**
 * Enumerates the possible result states for processing a notification.
 */
export var DataCollectionEventResult;
(function (DataCollectionEventResult) {
    /**
     * Notification processing succeeded.
     */
    DataCollectionEventResult["success"] = "success";
    /**
     * Notification processing partially succeeded.
     * Typically due to a 206 partial success from telemetry service,
     * indicating that one or more event details in the notification failed schema validation.
     */
    DataCollectionEventResult["partialSuccess"] = "partialSuccess";
    /**
     * Notification processing failed.
     */
    DataCollectionEventResult["failure"] = "failure";
})(DataCollectionEventResult || (DataCollectionEventResult = {}));
export var EventFailureReason;
(function (EventFailureReason) {
    EventFailureReason["queueRetryExceeded"] = "queueRetryExceeded";
    EventFailureReason["unableToObtainFilters"] = "unableToObtainFilters";
    EventFailureReason["eventExpired"] = "eventExpired";
    EventFailureReason["unableToSendToTelemetry"] = "unableToSendToTelemetry";
    EventFailureReason["localSchemaValidationFailure"] = "localSchemaValidationFailure";
    EventFailureReason["remoteSchemaValidationFailure"] = "remoteSchemaValidationFailure";
})(EventFailureReason || (EventFailureReason = {}));
/**
 * Indicates the type of error that occurred for the Data Collection plugin
 */
export var ServiceError;
(function (ServiceError) {
    /**
     * Indicates that an invalid configuration was attempted for a session's current status
     */
    ServiceError["invalidConfiguration"] = "invalidConfiguration";
    /**
     * Indicates that an invalid or malformed event was attempted for a session's current status
     */
    ServiceError["invalidEvent"] = "invalidEvent";
    /**
     *Indicates that an invalid profile request was attempted for a session's current status
     s*/
    ServiceError["profileRequestFailed"] = "profileRequestFailed";
    /**
     * Indicates that the event failed when was sent for a session's current status
     */
    ServiceError["sendEventFailed"] = "sendEventFailed";
})(ServiceError || (ServiceError = {}));
/**
 * The action performed as part of the event
 */
export var Action;
(function (Action) {
    /**
     * Screen displayed action
     */
    Action["screenDisplayed"] = "ScreenDisplayed";
    /**
     * Modal window displayed action
     */
    Action["modalWindowDisplayed"] = "ModalWindowDisplayed";
    /**
     * Control accordian expanded action
     */
    Action["controlAccordianExpanded"] = "ControlAccordianExpanded";
    /**
     * Control accordian collapsed action
     */
    Action["controlAccordianCollapsed"] = "ControlAccordianCollapsed";
    /**
     * Control displayed action
     */
    Action["controlDisplayed"] = "ControlDisplayed";
    /**
     * Control button clicked action
     */
    Action["controlButtonClicked"] = "ControlButtonClicked";
    /**
     * Control tile clicked action
     */
    Action["controlTileClicked"] = "ControlTileClicked";
    /**
     * Control hyperlink clicked action
     */
    Action["controlHyperLinkClicked"] = "ControlHyperLinkClicked";
    /**
     * Control toggle enabled action
     */
    Action["controlToggleEnabled"] = "ControlToggleEnabled";
    /**
     * Control toggle disabled action
     */
    Action["controlToggleDisabled"] = "ControlToggleDisabled";
    /**
     * Module initialization action
     */
    Action["moduleInitialized"] = "ModuleInitialized";
    /**
     * Module displayed action
     */
    Action["moduleDisplayed"] = "ModuleDisplayed";
})(Action || (Action = {}));
//# sourceMappingURL=datacollection.js.map